/** Table view with sortable headers and dynamic column visibility. Uses GridList for accessible rows. */
import { useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '../Button';
import { GridList, GridListItem } from '../GridList';
import { useDataView } from './DataView';
import type { Selection } from 'react-aria-components';

// ─── Sortable Header ────────────────────────────────────────────────────────

function SortableHeader({
  label,
  sortField,
  currentSort,
  currentDirection,
  onSort,
}: {
  label: string;
  sortField: string;
  currentSort?: string;
  currentDirection: 'asc' | 'desc';
  onSort: (field: string, dir: 'asc' | 'desc') => void;
}) {
  const isActive = currentSort === sortField;
  const SortIcon = isActive ? (currentDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <Button
      variant="ghost"
      className="h-auto min-w-0 gap-1 px-0 py-0 text-xs font-medium"
      style={{ color: isActive ? 'var(--f-accent-text)' : undefined }}
      onPress={() => {
        if (isActive) {
          onSort(sortField, currentDirection === 'asc' ? 'desc' : 'asc');
        } else {
          onSort(sortField, sortField === 'title' ? 'asc' : 'desc');
        }
      }}
    >
      {label}
      <SortIcon size={12} strokeWidth={2} style={{ transform: 'translateZ(0)' }} />
    </Button>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

const GRID_CLASS = 'grid items-center justify-items-start gap-3';

export function DataViewTable({ className }: { className?: string }) {
  const {
    items,
    columns,
    visibleColumns,
    sortBy,
    sortDirection,
    onSort,
    onItemClick,
    getItemKey,
    getItemTextValue,
  } = useDataView();

  const activeColumns = useMemo(
    () => columns.filter((c) => c.alwaysVisible || visibleColumns.has(c.key)),
    [columns, visibleColumns]
  );

  const gridStyle = useMemo(
    () => ({ gridTemplateColumns: activeColumns.map((c) => c.width).join(' ') }),
    [activeColumns]
  );

  function handleSelection(keys: Selection) {
    if (keys === 'all' || !onItemClick) return;
    const selectedKey = [...keys][0] as string | undefined;
    if (selectedKey) {
      const item = items.find((it) => getItemKey(it) === selectedKey);
      if (item) onItemClick(item);
    }
  }

  return (
    <div className={className}>
      {/* Header */}
      <div
        className={`${GRID_CLASS} border-b px-4 py-2 text-xs font-medium`}
        style={{ ...gridStyle, color: 'var(--f-fg-muted)', borderColor: 'var(--f-border)' }}
      >
        {activeColumns.map((col) =>
          col.sortField && onSort ? (
            <SortableHeader
              key={col.key}
              label={col.label}
              sortField={col.sortField}
              currentSort={sortBy}
              currentDirection={sortDirection}
              onSort={onSort}
            />
          ) : (
            <span key={col.key}>{col.label}</span>
          )
        )}
      </div>

      {/* Body */}
      <GridList
        aria-label="Data table"
        selectionMode={onItemClick ? 'single' : 'none'}
        onSelectionChange={handleSelection}
        items={items as object[]}
        dependencies={[visibleColumns]}
        className="flex flex-col gap-0"
      >
        {(item) => (
          <GridListItem
            key={getItemKey(item)}
            id={getItemKey(item)}
            textValue={getItemTextValue?.(item) ?? getItemKey(item)}
            unstyled
            className="border-b border-[var(--f-border)] bg-transparent hover:bg-[var(--f-bg-sunken)]"
          >
            <div className={`${GRID_CLASS} px-4 py-2.5`} style={gridStyle}>
              {activeColumns.map((col) => (
                <div key={col.key} className="min-w-0">
                  {col.render(item)}
                </div>
              ))}
            </div>
          </GridListItem>
        )}
      </GridList>
    </div>
  );
}
